import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Col, Form, Row } from 'antd';
import { sumBy } from 'es-toolkit';

import FormInputNumber from '@/views/components/Inputs/FormInputNumber';

import { convertToFloat, valueToNumber } from '../../../../../../utilz';
import { LDClaimFinancialCalculation } from '../../../../../../utilz/PreClaimsUtils';
import FormInput from '../../../../../components/Inputs/FormInput';
import HiddenFormInput from '../../../../../components/Inputs/HiddenFormInput';
import ToggleSwitch from '../../../../../components/Switch/ToggleSwitch';
import TMSFiledCard from '../../../../common/components/TMSFiledCard';
import { useGetLADCustomerSettings } from '../../../hooks';

const LADClaimFinancialsForm = ({ form, editMode, items, detailsData }) => {
  const disableItems = Form.useWatch('disableItems', form);
  const disableInterest = Form.useWatch('disableInterest', form);
  const disableProration = Form.useWatch('disableProration', form);
  const disableLabor = Form.useWatch('disableLabor', form);
  const disableInspection = Form.useWatch('disableInspection', form);
  const disableDocumentation = Form.useWatch('disableDocumentation', form);
  const claimMinCharge = Form.useWatch('claimMinCharge', form);
  const { selectedCompany } = useSelector((state) => state.auth);
  const { data: customerSettings } = useGetLADCustomerSettings(selectedCompany?.companyId);

  const handleToggleSwitch = (name) => (value) => {
    form.setFieldValue(name, value);
    if (
      (['disableInterest', 'disableProration', 'disableLabor', 'disableInspection', 'disableDocumentation'].includes(
        name,
      ) &&
        !value) ||
      (name === 'disableItems' && !value)
    ) {
      handleFinancialCalculation();
    } else if (name === 'disableItems' && value) {
      form.setFieldsValue({
        markdownAmount: convertToFloat(0),
        productClaimAmount: convertToFloat(0),
        repairCostAmount: convertToFloat(0),
        salvageAmount: convertToFloat(0),
      });
      handleFinancialCalculation();
    }
  };

  const handleFinancialCalculation = () => {
    setTimeout(() => {
      const financialData = LDClaimFinancialCalculation(form.getFieldsValue(), customerSettings, items, detailsData);
      form.setFieldsValue(financialData);
    }, 100);
  };

  const sumOfItemsWeights = useMemo(() => sumBy(items || [], ({ totalWeight }) => valueToNumber(totalWeight)), [items]);

  return (
    <div>
      <Row className="mb-10" gutter={15} justify="start">
        <Col style={{ width: '20%' }}>
          <FormInputNumber
            // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
            tabIndex="0"
            inputType="number"
            margin="mb-10"
            label="Max Liability"
            name="claimMaxLiability"
            editMode={editMode}
            precision={2}
            onChange={handleFinancialCalculation}
          />
          <FormInputNumber
            // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
            tabIndex="0"
            inputType="number"
            margin="mb-10"
            label="Pieces"
            name="claimNoOfPieces"
            editMode={editMode}
            maxLength={25}
            precision={2}
            onChange={handleFinancialCalculation}
          />
          <FormInputNumber
            // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
            tabIndex="0"
            inputType="number"
            margin="mb-10"
            label="Chargeable Weight"
            name="cargoShipmentWeight"
            editMode={editMode}
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              {
                message: 'Please enter Chargeable Weight',
                required: true,
              },
              () => ({
                validator(_, value) {
                  if (value && value < sumOfItemsWeights) {
                    return Promise.reject(
                      new Error('The Total Shipment Weight cannot be less than the Sum of the Claim Items Weight!'),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            onChange={handleFinancialCalculation}
          />
          <FormInput
            // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
            tabIndex="0"
            inputType="number"
            margin="mb-10"
            label="Freight Cost"
            name="claimFreightCharge"
            editMode={editMode}
            onBlur={handleFinancialCalculation}
          />
        </Col>
        <Col style={{ width: '20%' }}>
          <FormInputNumber
            tabIndex={disableProration && editMode ? 10004 : -1}
            inputType="number"
            margin="mb-10"
            label="Pro Rated"
            name="freightChargeClaimed"
            editMode={disableProration && editMode}
            precision={2}
            onChange={handleFinancialCalculation}
          />
          <FormInputNumber
            tabIndex={disableLabor && editMode ? 10005 : -1}
            inputType="number"
            margin="mb-10"
            label="Labor Cost"
            name="laborCharge"
            precision={2}
            editMode={disableLabor && editMode}
            onChange={handleFinancialCalculation}
          />
          <FormInputNumber
            // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
            tabIndex="0"
            inputType="number"
            margin="mb-10"
            label="Inspection Hours"
            name="inspectionHours"
            editMode={editMode}
            precision={2}
            onChange={() => {
              if (!disableInspection) {
                handleFinancialCalculation();
              }
            }}
          />
          <FormInputNumber
            tabIndex={disableInspection && editMode ? 10006 : -1}
            inputType="number"
            margin="mb-10"
            label="Inspection Cost"
            name="inspectionCost"
            editMode={disableInspection && editMode}
            precision={2}
            onChange={handleFinancialCalculation}
          />
        </Col>
        <Col style={{ width: '20%' }}>
          <FormInputNumber
            tabIndex={disableInterest && editMode ? 10008 : -1}
            inputType="number"
            margin="mb-10"
            label="Interest Expense"
            name="interestExpense"
            editMode={disableInterest && editMode}
            precision={2}
            onChange={() => {
              if (disableInterest) {
                handleFinancialCalculation();
              }
            }}
          />
          <FormInputNumber
            tabIndex={disableDocumentation && editMode ? 10009 : -1}
            inputType="number"
            margin="mb-10"
            precision={2}
            label="Documentation Cost"
            name="claimAdminCharge"
            editMode={disableDocumentation && editMode}
            onChange={() => {
              if (disableDocumentation) {
                handleFinancialCalculation();
              }
            }}
          />
          <FormInputNumber
            // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
            tabIndex="0"
            inputType="number"
            margin="mb-10"
            precision={2}
            label="Tax"
            name="claimTaxAmount"
            editMode={editMode}
            onBlur={handleFinancialCalculation}
          />
          <FormInputNumber
            // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
            tabIndex="0"
            inputType="number"
            margin="mb-10"
            precision={2}
            label="Additional Cost"
            name="claimAddCharge"
            editMode={editMode}
            onBlur={handleFinancialCalculation}
          />
        </Col>
        <Col style={{ width: '20%' }}>
          <FormInputNumber
            // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
            tabIndex="0"
            inputType="number"
            margin="mb-10"
            precision={2}
            label="Deductible"
            name="claimDeductibleAmount"
            editMode={editMode}
            onBlur={handleFinancialCalculation}
          />
          <FormInputNumber
            // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
            tabIndex="0"
            inputType="number"
            margin="mb-10"
            precision={2}
            label="Credit"
            name="claimCreditAmount"
            editMode={editMode}
            onBlur={handleFinancialCalculation}
          />
          <FormInputNumber
            tabIndex={disableItems && editMode ? 10014 : -1}
            inputType="number"
            margin="mb-10"
            label="Salvage Allowance"
            precision={2}
            name="salvageAmount"
            editMode={disableItems && editMode}
            onChange={handleFinancialCalculation}
          />
          <FormInputNumber
            tabIndex={disableItems && editMode ? 10015 : -1}
            inputType="number"
            margin="mb-10"
            label="Markdown Amount"
            name="markdownAmount"
            precision={2}
            editMode={disableItems && editMode}
          />
        </Col>
        <Col style={{ width: '20%' }}>
          <FormInputNumber
            tabIndex={disableItems && editMode ? 10016 : -1}
            inputType="number"
            margin="mb-10"
            precision={2}
            label="Repair Cost"
            name="repairCostAmount"
            editMode={disableItems && editMode}
            onChange={handleFinancialCalculation}
          />
          <FormInputNumber
            tabIndex={disableItems && editMode ? 10017 : -1}
            inputType="number"
            margin="mb-10"
            precision={2}
            label="Product Amount"
            name="productClaimAmount"
            editMode={disableItems && editMode}
            onChange={handleFinancialCalculation}
          />
          <FormInputNumber
            // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
            tabIndex="0"
            inputType="number"
            margin="mb-10"
            label="Labor Hours"
            name="laborHours"
            editMode={editMode}
            precision={2}
            onChange={() => {
              if (!disableLabor) {
                handleFinancialCalculation();
              }
            }}
          />
          <FormInputNumber
            tabIndex={disableItems && editMode ? 10019 : -1}
            inputType="number"
            margin="mb-10"
            precision={2}
            label="Total Claim"
            name="claimTotal"
            editMode={disableItems && editMode}
            onChange={handleFinancialCalculation}
          />
        </Col>
      </Row>
      <Row gutter={15} justify="start">
        <Col style={{ width: '20%' }}>
          <TMSFiledCard containerClass="mb-10" title="Manual Items">
            <ToggleSwitch
              // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
              tabIndex="0"
              checkedChildren="On"
              unCheckedChildren="Off"
              checked={disableItems}
              onClick={handleToggleSwitch('disableItems')}
              disabled={!editMode}
            />
          </TMSFiledCard>
        </Col>
        <Col style={{ width: '20%' }}>
          <TMSFiledCard containerClass="mb-10" title="Manual Interest">
            <ToggleSwitch
              // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
              tabIndex="0"
              checkedChildren="On"
              unCheckedChildren="Off"
              checked={disableInterest}
              onClick={handleToggleSwitch('disableInterest')}
              disabled={!editMode}
            />
          </TMSFiledCard>
        </Col>
        <Col style={{ width: '20%' }}>
          <TMSFiledCard containerClass="mb-10" title="Manual Pro Ration">
            <ToggleSwitch
              // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
              tabIndex="0"
              checkedChildren="On"
              unCheckedChildren="Off"
              checked={disableProration}
              onClick={handleToggleSwitch('disableProration')}
              disabled={!editMode}
            />
          </TMSFiledCard>
        </Col>
        <Col style={{ width: '20%' }}>
          <TMSFiledCard containerClass="mb-10" title="Manual Documentation">
            <ToggleSwitch
              // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
              tabIndex="0"
              checkedChildren="On"
              unCheckedChildren="Off"
              checked={disableDocumentation}
              onClick={handleToggleSwitch('disableDocumentation')}
              disabled={!editMode}
            />
          </TMSFiledCard>
        </Col>
        <Col style={{ width: '20%' }}>
          <TMSFiledCard containerClass="mb-10" title="Minimum Charge">
            <ToggleSwitch
              // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
              tabIndex="0"
              checkedChildren="On"
              unCheckedChildren="Off"
              checked={claimMinCharge}
              onClick={handleToggleSwitch('claimMinCharge')}
              disabled={!editMode}
            />
          </TMSFiledCard>
        </Col>
        <Col style={{ width: '20%' }}>
          <TMSFiledCard title="Manual Labor">
            <ToggleSwitch
              // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
              tabIndex="0"
              checkedChildren="On"
              unCheckedChildren="Off"
              checked={disableLabor}
              onClick={handleToggleSwitch('disableLabor')}
              disabled={!editMode}
            />
          </TMSFiledCard>
        </Col>
        <Col style={{ width: '20%' }}>
          <TMSFiledCard title="Manual Inspection">
            <ToggleSwitch
              // biome-ignore lint/a11y/noPositiveTabindex: TODO: fix
              tabIndex="0"
              checkedChildren="On"
              unCheckedChildren="Off"
              checked={disableInspection}
              onClick={handleToggleSwitch('disableInspection')}
              disabled={!editMode}
            />
          </TMSFiledCard>
        </Col>
      </Row>
      <HiddenFormInput name="disableItems" />
      <HiddenFormInput name="disableInterest" />
      <HiddenFormInput name="disableProration" />
      <HiddenFormInput name="disableLabor" />
      <HiddenFormInput name="disableInspection" />
      <HiddenFormInput name="disableDocumentation" />
      <HiddenFormInput name="claimMinCharge" />
    </div>
  );
};

export default LADClaimFinancialsForm;
